import { ErrorMessage, Formik, FormikHelpers as FormikActions } from 'formik'
import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import * as Yup from 'yup'
import { ValidationError } from 'epc-schema'
import { Content, Form, Indicators, Input } from 'ui'
import { RequiredEmail, RequiredString } from 'validations'
import {
  ContentAreaGroupsRenderer,
  DistrictSelectField,
  DistrictSelectFn,
  SchoolSelectField,
  SchoolSelectFn,
} from '../FormControls'
import { useCurrentProfile, useUpdateProfile } from '../graphql'

/* BEGIN - Form Values */
/**
 * This interface represents potential form values and
 * is passed into our custom form field factories to get
 * type safety on our form.
 */
interface IEducatorStepOneFormValues {
  [key: string]: any | string | string[] | undefined
  about?: string | undefined
  bio?: string | undefined
  contentAreas?: string[] | undefined
  emailAddress?: string | undefined
  phone?: string | undefined
  keywords?: string | undefined
  role?: string | undefined
  schoolId?: string | undefined
  schoolName?: string | undefined
  districtId?: string | undefined
  districtName?: string | undefined
  gradeLevels?: string[] | undefined
}

/**
 * This schema is used by formik to provide client side
 * form validation.
 */
const EducatorStepOneSchema = Yup.object().shape({
  about: RequiredString(),
  contentAreas: Yup.array()
    .of(RequiredString())
    .min(1, 'must have at least 1 choice'),
  districtName: RequiredString(),
  emailAddress: RequiredEmail(),
  firstName: RequiredString(),
  gradeLevels: Yup.array()
    .of(RequiredString())
    .min(1, 'must have at least 1 choice'),
  keywords: RequiredString(),
  lastName: RequiredString(),
  phone: RequiredString().min(14, 'Must be a valid phone number'),
  schoolName: RequiredString(),
})

/**
 * These are the actual default values supplied to the form
 * if a profile is not loaded via the network. Any missing
 * values from the network fetched profile will have these
 * values substituted as well.
 */
const blankValues: IEducatorStepOneFormValues = {
  about: '',
  companyName: '',
  contentAreas: [],
  districtId: '',
  districtName: '',
  email: '',
  firstName: '',
  gradeLevels: [],
  id: '',
  keywords: '',
  lastName: '',
  phone: '',
  role: '',
  schoolId: '',
  schoolName: '',
}
/* END - Form Values */

/* BEGIN - Custom Form Renderers */
const FormField = Input.Renderer<IEducatorStepOneFormValues>()
const CheckBoxGroupField =
  Input.CheckBoxGroupRenderer<IEducatorStepOneFormValues>()
const ContentAreaGroups =
  ContentAreaGroupsRenderer<IEducatorStepOneFormValues>()
const ErrorSummary = Form.ErrorSummaryRenderer<IEducatorStepOneFormValues>()
/* END - Custom Form Renderers */

/**
 * The initial step for creating an account as a teacher.
 */
const FormView: React.FC<RouteComponentProps> = (props) => {
  const [updateProfile, { error: mutationError }] = useUpdateProfile()

  const handleSubmitForm = async (
    values: IEducatorStepOneFormValues,
    actions: FormikActions<any>
  ) => {
    const result = await updateProfile({
      variables: {
        profileInput: {
          about: values.about,
          contentAreas: values.contentAreas || [],
          districtId: values.districtId,
          districtName: values.districtName,
          emailAddress: values.emailAddress,
          firstName: values.firstName as string,
          gradeLevels: values.gradeLevels,
          id: values.id as string,
          keywords: [...(values.keywords || '').split(',')] as string[],
          lastName: values.lastName as string,
          phone: values.phone,
          schoolId: values.schoolId,
          schoolName: values.schoolName,
        },
      },
    })
    if (
      result.data &&
      !result.data.response.success &&
      result.data.response.errors
    ) {
      result.data.response.errors
        .map((e) => e as ValidationError)
        .forEach((e) => actions.setFieldError(e.path, e.message))
    } else {
      props.history.push('/signup/educator/two')
    }
    actions.setSubmitting(false)
  }

  const {
    educator,
    profileErrors,
    error: queryError,
    getCurrentProfile,
    loading,
  } = useCurrentProfile()

  useEffect(() => {
    getCurrentProfile()
  }, [getCurrentProfile])

  if (loading) {
    return <Indicators.Spinner />
  }

  if (queryError) {
    return (
      <Form.ErrorComponent error={queryError ? queryError.message : null} />
    )
  }

  const message =
    profileErrors && (profileErrors[0] || { message: null }).message
  if (message) {
    return <Form.ErrorComponent error={message} />
  }

  const defaultValues = (educator as IEducatorStepOneFormValues) || blankValues
  const initialValues = {
    ...Object.keys(defaultValues).reduce(
      (o, k) => ({ ...o, [k]: defaultValues[k] || o[k] }),
      blankValues
    ),
    keywords: ((educator && educator.keywords) || ['']).join(','),
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      validationSchema={EducatorStepOneSchema}
    >
      {({
        errors,
        handleSubmit,
        isSubmitting,
        values,
        setFieldValue,
        submitCount,
        submitForm,
      }) => {
        const onDismiss = (name: string) => () => null
        const selectDistrict: DistrictSelectFn = (district) => {
          setFieldValue('districtId', district.id)
          setFieldValue('districtName', district.name)
          setFieldValue('schoolId', '')
          setFieldValue('schoolName', '')
        }
        const selectSchool: SchoolSelectFn = (school) => {
          setFieldValue('schoolId', school.id)
          setFieldValue('schoolName', school.name)
        }
        return (
          <Form.Form onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Item width={[1, 3 / 7, 1 / 3]}>
                <FormField
                  disabled={isSubmitting}
                  name="firstName"
                  label="First Name"
                  type="text"
                  required={true}
                />
              </Form.Item>
              <Form.Item width={[1, 4 / 7, 2 / 3]}>
                <FormField
                  disabled={isSubmitting}
                  name="lastName"
                  label="Last Name"
                  type="text"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={1}>
                <FormField
                  disabled={true}
                  name="emailAddress"
                  label="Email Address"
                  placeholder="my@emailaddress.com"
                  type="email"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={[1, 1 / 4]}>
                <FormField
                  disabled={isSubmitting}
                  name="phone"
                  label="Phone"
                  type="text"
                  component={Input.RendererComponent.Masked}
                  mask={Input.Masks.PhoneNumber}
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={1}>
                <DistrictSelectField
                  required={true}
                  disabled={isSubmitting}
                  name="districtId"
                  label="School District"
                  value={values.districtName || ''}
                  placeholder="Choose your district"
                  onSelect={selectDistrict}
                  onDismiss={onDismiss('district')}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={1}>
                <SchoolSelectField
                  required={true}
                  disabled={isSubmitting || !values.districtId}
                  name="schoolId"
                  districtId={values.districtId || ''}
                  label="Name of School"
                  value={values.schoolName || ''}
                  placeholder={
                    values.districtId
                      ? 'Choose your school'
                      : 'First choose your district'
                  }
                  onSelect={selectSchool}
                  onDismiss={onDismiss('school')}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item>
                <CheckBoxGroupField
                  disabled={isSubmitting}
                  label="Grade Levels You Teach"
                  required={true}
                  inline={true}
                  inputWidth={1 / 2}
                  name="gradeLevels"
                  values={[
                    { label: 'K-2', value: 'K-2' },
                    { label: '9-10', value: '9-10' },
                    { label: '3-5', value: '3-5' },
                    { label: '11-12', value: '11-12' },
                    { label: '6-8', value: '6-8' },
                  ]}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item>
                <Form.Label>Subjects/Content Areas</Form.Label>
                <Form.Descriptive>
                  Subjects and/or content areas you teach, to be matched with
                  our database of industry professionals
                  <ErrorMessage name="contentAreas" component={Input.Error} />
                </Form.Descriptive>
              </Form.Item>
            </Form.Row>
            <ContentAreaGroups name="contentAreas" disabled={isSubmitting} />
            <Form.Row>
              <Form.Item>
                <FormField
                  component={Input.RendererComponent.TextArea}
                  description={
                    <React.Fragment>
                      One or two sentences about you and your interests that
                      will accompany your profile in search results, events,
                      etc. <Content.Link to="#">Help me</Content.Link>
                    </React.Fragment>
                  }
                  disabled={isSubmitting}
                  name="about"
                  label="Tell Us About Yourself"
                  placeholder=""
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item>
                <FormField
                  component={Input.RendererComponent.TextArea}
                  description={
                    <React.Fragment>
                      Have a specific focus or other interests that industry
                      professionals should know about? List them here.
                    </React.Fragment>
                  }
                  disabled={isSubmitting}
                  name="keywords"
                  label="Keywords & Phrases"
                  placeholder="keywords & phrases, separated by commas, e.g. civil engineer, structural engineer, bioscience, medical sciences"
                  required={true}
                />
              </Form.Item>
            </Form.Row>

            <Form.GeneralError name="_" />
            <Form.ErrorComponent
              error={mutationError ? mutationError.message : null}
            />
            {submitCount > 0 ? <ErrorSummary errors={errors} /> : null}

            <Form.Row>
              <Form.Item width={1}>
                <Content.ActionButton
                  type="submit"
                  loading={isSubmitting}
                  onClick={submitForm}
                  label="Save and go to Step 2"
                />
              </Form.Item>
            </Form.Row>
          </Form.Form>
        )
      }}
    </Formik>
  )
}

export const EducatorStepOne = withRouter(FormView)
